// React
import { Fragment, useEffect, useMemo, useState } from "react";

// Third-Party
import { Box, Button, Divider, Grid, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";

// Internal
import { DialogComponent } from "@components/Dialog";
import MySwitchField from "@components/form/switch";
import { RHFAutocomplete, RHFTextField } from "@components/hook-form";
import { formatPeppolOptions } from "@pages/ManualDocumentV2/utils/peppolOptions";
import { currencySymbols } from "@utils/currency";
import RHFCountrySelect from "src/components/hook-form/RHFCountrySelect";

// Types
import { Option } from "@components/hook-form/RHFAutocomplete";
import { IFormEngine, ISectionField } from "@pages/ManualDocumentV2/core";

interface IEditVendorInformationProps {
  open: boolean;
  onClose: () => void;
  translate: (text: any, options?: any) => string;
  engine: IFormEngine;
  vendorCompanyIDOptions: Option[];
  hiddenFields?: string[];
  extraFields?: ISectionField[];
}

const ADDRESS_FIELDS = [
  "vendorCompanyStreet",
  "vendorCompanyStreetNumber",
  "vendorCompanyLocality",
  "vendorCompanyZipCode",
  "vendorCompanyRegion",
];

export const EditVendorInformation = ({
  open,
  onClose,
  translate,
  engine,
  vendorCompanyIDOptions,
  hiddenFields,
  extraFields,
}: IEditVendorInformationProps) => {
  const { getValues, setValue, watch } = useFormContext<any>();
  const isLogoURL = Boolean(getValues("logoURL"));
  const [isShowLogoURL, setIsShowLogoURL] = useState(false);
  const isAccountPayable = engine.config.accountClass === "AccountPayable";
  const isFrance = engine.config.complianceStandardKey === "FrancePA";

  const hidden = useMemo(() => new Set(hiddenFields ?? []), [hiddenFields]);
  const isVisible = (key: string) => !hidden.has(key);
  const showAddressSection = ADDRESS_FIELDS.some(isVisible);
  const peppolOptionsFormatted: Option[] = useMemo(() => {
    return formatPeppolOptions(engine.peppolOptions);
  }, [engine.peppolOptions]);

  const syncPeppolFields = (selectedId?: string) => {
    const option = selectedId
      ? engine.peppolOptions?.find(
          (opt) => opt.peppolParticipantId === selectedId,
        )
      : null;

    setValue("vendorPeppolInboxCountry", option?.country || "", {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("vendorPeppolSchemeId", option?.schemeId || "", {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("vendorPeppolParticipantId", option?.peppolParticipantId || "", {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (open) {
      const currentParticipant = getValues("vendorPeppolParticipantId");
      if (currentParticipant && peppolOptionsFormatted.length > 0) {
        const match = peppolOptionsFormatted.find(
          (opt) => opt.value === currentParticipant,
        );
        const currentVal = getValues("_peppolSelector");
        const currentId =
          typeof currentVal === "object" ? currentVal?.value : currentVal;

        if (match && currentId !== currentParticipant) {
          setValue("_peppolSelector", match);
        }
      }
    }
  }, [open, peppolOptionsFormatted, getValues, setValue]);

  const selectedPeppolObj = watch("_peppolSelector");

  useEffect(() => {
    if (selectedPeppolObj !== undefined) {
      const selectedId =
        typeof selectedPeppolObj === "object"
          ? selectedPeppolObj?.value
          : selectedPeppolObj;
      syncPeppolFields(selectedId || undefined);
    }
  }, [selectedPeppolObj, engine.peppolOptions, setValue]);

  useEffect(() => {
    if (isLogoURL) setIsShowLogoURL(true);
  }, [isLogoURL]);

  return (
    <DialogComponent
      isShowModal={open}
      showModalMethod={onClose}
      maxWidth="md"
      fullWidth
      contentSx={{ p: 0 }}
      dialogTitle={translate("edit_vendor_information")}
      dialogSubTitle={translate("fill_vendor_details")}
      dialogContent={
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <RHFTextField
                name="vendorCompanyName"
                label={translate("vendor_company_name")}
                fullWidth
              />
            </Grid>
            {isVisible("vendorCompanyID") && (
              <Grid item xs={12} md={6}>
                <RHFAutocomplete
                  options={vendorCompanyIDOptions}
                  name="vendorCompanyID"
                  label={translate("vendor_company_ID")}
                  fullWidth
                  defaultValue={null}
                  freeSolo
                  autoSelect
                />
              </Grid>
            )}
            {isVisible("vendorEmail") && (
              <Grid item xs={12} md={6}>
                <RHFTextField
                  name="vendorEmail"
                  label={translate("vendor_email_address")}
                  fullWidth
                />
              </Grid>
            )}
            {isVisible("vendorCountry") && (
              <Grid item xs={12} sm={6}>
                <RHFCountrySelect
                  name="vendorCountry"
                  label={translate("country")}
                />
              </Grid>
            )}
            {isAccountPayable && (
              <Grid item xs={12} sm={6}>
                <RHFAutocomplete
                  name="currency"
                  label={translate("currency")}
                  options={Object.entries(currencySymbols)
                    ?.filter(([key]) => !!key)
                    .map(([key, value]) => ({
                      value: key,
                      label: `${key} ${value}`,
                    }))}
                  freeSolo
                  autoSelect
                />
              </Grid>
            )}
            {isAccountPayable && (
              <Grid item xs={12} sm={6}>
                <RHFTextField
                  name="vendorCompanyAddress"
                  label={translate("companyAddress")}
                  fullWidth
                />
              </Grid>
            )}

            {isVisible("logoURL") && (
              <Grid
                item
                xs={12}
                sm={6}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <MySwitchField
                  checked={isShowLogoURL}
                  label={
                    <Typography variant="body2">
                      {translate(`add_logo_url`)}
                    </Typography>
                  }
                  onChange={() => setIsShowLogoURL((v) => !v)}
                  labelPlacement="end"
                  sx={{ justifyContent: "space-between", alignItems: "center" }}
                />
              </Grid>
            )}

            {isVisible("logoURL") && isShowLogoURL && (
              <Grid item xs={12} sm={6}>
                <RHFTextField
                  name="logoURL"
                  label={translate("logo_url")}
                  fullWidth
                />
              </Grid>
            )}

            {showAddressSection && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    {translate("vendor_address")}
                  </Typography>
                </Grid>

                {isVisible("vendorCompanyStreet") && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="vendorCompanyStreet"
                      label={translate("company_street")}
                      fullWidth
                    />
                  </Grid>
                )}
                {isVisible("vendorCompanyStreetNumber") && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="vendorCompanyStreetNumber"
                      label={translate("company_street_number")}
                      fullWidth
                    />
                  </Grid>
                )}
                {isVisible("vendorCompanyLocality") && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="vendorCompanyLocality"
                      label={translate("company_locality")}
                      fullWidth
                    />
                  </Grid>
                )}
                {isVisible("vendorCompanyZipCode") && (
                  <Grid item xs={12} md={6}>
                    <RHFTextField
                      name="vendorCompanyZipCode"
                      label={translate("company_zip_code")}
                      fullWidth
                    />
                  </Grid>
                )}
                {isVisible("vendorCompanyRegion") && (
                  <Grid item xs={12}>
                    <RHFTextField
                      name="vendorCompanyRegion"
                      label={translate("company_region")}
                      fullWidth
                    />
                  </Grid>
                )}
              </>
            )}

            {extraFields?.map((field) => {
              const node = field.render({ translate, engine });
              if (!field.grid) {
                return <Fragment key={field.id}>{node}</Fragment>;
              }
              return (
                <Grid
                  key={field.id}
                  item
                  xs={field.grid.xs ?? 12}
                  sm={field.grid.sm}
                  md={field.grid.md}
                  lg={field.grid.lg}
                  xl={field.grid.xl}
                >
                  {node}
                </Grid>
              );
            })}

            {/* Vendor Inbox for France */}
            {isFrance && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    {translate("vendor_inbox")}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <RHFAutocomplete
                    name="_peppolSelector"
                    label={translate("select_peppol_configuration")}
                    fullWidth
                    options={peppolOptionsFormatted}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      }
      dialogActions={
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="contained" onClick={onClose} size="small">
            {translate("update")}
          </Button>
        </Box>
      }
    />
  );
};
